import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useGameSocket } from 'hooks/useGameSocket';
import { useTableLayoutMode } from 'hooks/useTableLayoutMode';
import { useTableExitNavigation } from 'hooks/useTableExitNavigation';
import { syncTableViewportHeight } from 'hooks/useFullscreen';
import GameTable from 'components/GameTable';
import { PlayerAction } from 'constants/game';
import { lobbyPathForVariant } from 'lib/variant-assets';

export default function TablePage() {
  const { id } = useParams<{ id: string }>();
  const pageRef = useRef<HTMLDivElement>(null);
  const tableProfileId = Number(id);
  const {
    isFullscreen,
    isImmersiveActive,
    nativeFsChromeHint,
    exitFullscreen,
  } = useTableLayoutMode();

  const {
    connected,
    tableState,
    myCards,
    battleResult,
    sideShowResult,
    error,
    chatMessages,
    turnSeconds,
    turnMaxSeconds,
    handCountdownSeconds,
    handCountdownMax,
    setHandCountdownPaused,
    leaveTable,
    sendAction,
    sendChatMessage,
    clearBattleResult,
    clearError,
  } = useGameSocket(tableProfileId);

  const tableStateRef = useRef(tableState);
  tableStateRef.current = tableState;

  const lobbyPath = lobbyPathForVariant(tableState?.variantCode);

  const { handleBack, handleLeave, handleOpenWallet } = useTableExitNavigation({
    fallbackPath: lobbyPath,
    leaveTable,
    getBattleUuid: () => tableStateRef.current?.battleUuid,
    routePrefix: '/table/',
  });

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      void exitFullscreen();
    };
  }, [exitFullscreen]);

  useEffect(() => {
    const syncViewport = () => {
      syncTableViewportHeight();
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);
    return () => {
      window.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.visualViewport?.removeEventListener('scroll', syncViewport);
    };
  }, []);

  const handleAction = (action: PlayerAction, amount?: number) => {
    if (!tableState) return;
    sendAction(tableState.battleUuid, action, amount);
  };

  const pageClassName = [
    'tp-desktop-page fixed inset-0 z-50 flex flex-col overflow-hidden bg-tp-bg',
    isImmersiveActive ? 'tp-table-immersive' : '',
    isFullscreen ? 'tp-native-fullscreen' : '',
    nativeFsChromeHint ? 'tp-native-fs-chrome-hint' : '',
    'tp-table-portrait-mode',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={pageRef}
      className={pageClassName}
      style={{ height: 'calc(var(--tp-vh, 1dvh) * 100)', maxHeight: 'calc(var(--tp-vh, 1dvh) * 100)' }}
    >
      {!connected && (
        <div className="absolute left-1/2 top-16 z-[60] -translate-x-1/2 rounded-xl border border-tp-gold/30 bg-black/70 px-5 py-2 text-sm text-tp-gold backdrop-blur-md">
          Connecting to game server…
        </div>
      )}
      {error && (
        <div className="absolute left-1/2 top-16 z-[60] flex w-[min(90%,420px)] -translate-x-1/2 items-center justify-between rounded-xl bg-red-950/90 px-4 py-3 text-sm ring-1 ring-red-500/40">
          <span>{error}</span>
          <button type="button" onClick={clearError} className="text-white/70 underline">
            Dismiss
          </button>
        </div>
      )}
      {!tableState ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 animate-spin rounded-full border-2 border-tp-gold border-t-transparent" />
            <p className="mt-4 text-white/50">Joining table…</p>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <GameTable
            state={tableState}
            myCards={myCards}
            turnSeconds={turnSeconds}
            turnMaxSeconds={turnMaxSeconds}
            handCountdownSeconds={handCountdownSeconds}
            handCountdownMax={handCountdownMax}
            setHandCountdownPaused={setHandCountdownPaused}
            battleResult={battleResult}
            sideShowResult={sideShowResult}
            onClearBattleResult={clearBattleResult}
            onAction={handleAction}
            onBack={handleBack}
            onLeave={handleLeave}
            onOpenWallet={handleOpenWallet}
            chatMessages={chatMessages}
            onSendChatText={(text) =>
              tableState && sendChatMessage(tableState.battleUuid, { kind: 'text', text })
            }
            onSendChatPhrase={(phraseId) =>
              tableState && sendChatMessage(tableState.battleUuid, { kind: 'phrase', phraseId })
            }
            onSendChatSticker={(stickerId) =>
              tableState && sendChatMessage(tableState.battleUuid, { kind: 'sticker', stickerId })
            }
          />
        </div>
      )}
    </div>
  );
}
